import React from "react";
import { Box, Typography } from "@mui/material";

const colorMap = {
  black: "#000000",
  white: "#FFFFFF",
  gray: "#808080",
  grey: "#808080",
  silver: "#C0C0C0",
  maroon: "#800000",
  red: "#FF0000",
  purple: "#800080",
  fuchsia: "#FF00FF",
  green: "#008000",
  lime: "#00FF00",
  olive: "#808000",
  yellow: "#FFFF00",
  navy: "#000080",
  blue: "#0000FF",
  teal: "#008080",
  aqua: "#00FFFF",
  cyan: "#00FFFF",
  orange: "#FFA500",
  pink: "#FFC0CB",
  brown: "#A52A2A",
  beige: "#F5F5DC",
  coral: "#FF7F50",
  peach: "#FFDAB9",
  indigo: "#4B0082",
  violet: "#EE82EE",
  khaki: "#F0E68C",
  mint: "#98FF98",
  lavender: "#E6E6FA",
  charcoal: "#36454F",
  gold: "#FFD700",
  bronze: "#CD7F32",
  burgundy: "#800020",
  mustard: "#FFDB58",
  rust: "#B7410E",
  magenta: "#FF00FF",
  turquoise: "#40E0D0",
  cream: "#FFFDD0",
  offwhite: "#FAF9F6",
  "off-white": "#FAF9F6",
  tan: "#D2B48C",
  taupe: "#483C32",
  blush: "#DE5D83",
  mauve: "#E0B0FF",
};

const getHexForColor = (colorName) => {
  if (!colorName) return null;
  const lowerName = colorName.toLowerCase();
  
  // Exact match
  if (colorMap[lowerName]) return colorMap[lowerName];

  // Substring match
  for (const [key, hex] of Object.entries(colorMap)) {
    if (lowerName.includes(key)) {
      return hex;
    }
  }

  return null;
};

const ColorSelector = ({ colors, selectedColor, onSelectColor }) => {
  if (!colors || colors.length === 0) return null;

  const allColorsHaveHex = colors.every(colorName => getHexForColor(colorName) !== null);

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "Poppins-SemiBold",
          fontSize: "14px",
          color: "#1a1a1a",
          mb: 1.5,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Color: <span style={{ fontFamily: "Poppins-Regular", color: "#666", textTransform: "none" }}>{selectedColor}</span>
      </Typography>
      
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {colors.map((colorName, index) => {
          const hex = getHexForColor(colorName);
          const isSelected = selectedColor === colorName;

          if (allColorsHaveHex) {
            // Render circle
            return (
              <Box
                key={index}
                onClick={() => onSelectColor(colorName)}
                title={colorName}
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: hex,
                  border: hex === "#FFFFFF" || hex === "#FAF9F6" || hex === "#FFFDD0" ? "1px solid #ddd" : "1px solid rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  position: "relative",
                  "&::after": isSelected ? {
                    content: '""',
                    position: "absolute",
                    top: "-4px",
                    left: "-4px",
                    right: "-4px",
                    bottom: "-4px",
                    borderRadius: "50%",
                    border: "2px solid #FF6B00",
                  } : {},
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }
                }}
              />
            );
          } else {
            // Render text pill
            return (
              <Box
                key={index}
                onClick={() => onSelectColor(colorName)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  border: isSelected ? "2px solid #FF6B00" : "1px solid #eee",
                  backgroundColor: isSelected ? "rgba(255,107,0,0.05)" : "#f9f9f9",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#FF6B00",
                    backgroundColor: "rgba(255,107,0,0.05)",
                  }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: isSelected ? "Poppins-Medium" : "Poppins-Regular",
                    fontSize: "13px",
                    color: isSelected ? "#FF6B00" : "#333",
                  }}
                >
                  {colorName}
                </Typography>
              </Box>
            );
          }
        })}
      </Box>
    </Box>
  );
};

export default ColorSelector;
